import { createServerFn } from "@tanstack/react-start";

export type BrandingTheme = Partial<{
  night_deep: string;
  night: string;
  gold: string;
  amber_glow: string;
  cream: string;
  foreground: string;
  card: string;
}>;

export type PublicBranding = {
  logoUrl: string | null;
  logoFullUrl: string | null;
  faviconUrl: string | null;
  theme: BrandingTheme;
};

async function resolveUrl(path: string | null): Promise<string | null> {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data } = await supabaseAdmin.storage
    .from("branding")
    .createSignedUrl(path, 60 * 60 * 24 * 7);
  return data?.signedUrl ?? null;
}

export const getBranding = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicBranding> => {
    const { cached } = await import("@/lib/server-cache.server");
    return cached("branding", 300_000, async () => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("site_branding")
      .select("logo_url, logo_full_url, favicon_url, theme")
      .eq("id", "default")
      .maybeSingle();

    if (!data) return { logoUrl: null, logoFullUrl: null, faviconUrl: null, theme: {} };

    const [logoUrl, logoFullUrl, faviconUrl] = await Promise.all([
      resolveUrl(data.logo_url as string | null),
      resolveUrl(data.logo_full_url as string | null),
      resolveUrl(data.favicon_url as string | null),
    ]);

    return {
      logoUrl,
      logoFullUrl,
      faviconUrl,
      theme: (data.theme as BrandingTheme) ?? {},
    };
    });
  },
);